import { FC, useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  kycRefusalLocales,
  kycRefusalResources,
} from '@shinetools/kyc-validation';
import LocalizedStrings from 'localized-strings';

import useCompanyContext from 'common/hooks/useCompanyContext';
import Button from 'components/_core/Button';
import Column from 'components/Column';
import DecorativeImage from 'components/DecorativeImage';
import Loader from 'components/Loader';
import Modal from 'components/Modal';
import OpenHelpButton from 'features/Help/components/OpenHelpButton';
import cannotOpenAccountSrc from 'features/Onboarding/images/cannotOpenAccount.svg';
import invalidKycSrc from 'features/Onboarding/images/invalidKyc.svg';
import Page from 'features/Onboarding/legacy/components/Page';
import { OnboardingPageContent } from 'features/Onboarding/pageLayoutStyles';

import PageProps from '../PageProps';
import { KYB_REFUSAL_TAG_QUERY } from './graphql';
import locales from './locales';
import {
  CannotOpenAccountTitle as Title,
  ImageContainer,
  MessageText as SubTitle,
  ModalContent,
} from './styles';
import { CannotOpenAccountData, CannotOpenAccountVariables } from './types';

const kycRefusalMessages = new LocalizedStrings(kycRefusalLocales);
const kycRefusalResourcesLocales = new LocalizedStrings(kycRefusalResources);

const ResourceLink: FC<{ refusalTag?: string | null }> = ({ refusalTag }) => {
  // @ts-expect-error Cannot invoke an object which is possibly 'undefined'.
  const resource = kycRefusalResourcesLocales.getLocalizedUrl(refusalTag);
  if (!resource) {
    return null;
  }

  return (
    <p>
      <a href={resource.url} rel="noopener noreferrer" target="_blank">
        {resource.label}
      </a>
    </p>
  );
};

const CannotOpenAccount: FC<PageProps> = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { companyProfileId } = useCompanyContext();

  const { data, loading } = useQuery<
    CannotOpenAccountData,
    CannotOpenAccountVariables
  >(KYB_REFUSAL_TAG_QUERY, {
    skip: !companyProfileId,
    variables: {
      companyProfileId,
    },
  });
  const refusalTag = data?.viewer.company.lastKybReview?.refusalTag;
  const error = data?.viewer.company.lastKybReview?.error;

  // @ts-expect-error Cannot invoke an object which is possibly 'undefined'.
  const localizedRefusalTag = kycRefusalMessages.getLocalizedString(refusalTag);
  const localizedRefusalMessage =
    refusalTag === 'CUSTOM'
      ? // The service enforces `error` to be not null if `refusalReason` is set to CUSTOM,
        // so it's safe to cast it as a string here. LocalizedStrings requires the param to be not null
        kycRefusalMessages.formatString(localizedRefusalTag, error as string)
      : localizedRefusalTag;

  if (isModalOpen) {
    return (
      <Modal onClose={() => setIsModalOpen(false)}>
        <ModalContent>
          <Column alignItems="center">
            {loading ? <Loader /> : null}
            <ImageContainer>
              <DecorativeImage
                heightPx={200}
                src={cannotOpenAccountSrc}
                widthPx={310}
              />
            </ImageContainer>
            <Title>{locales.title}</Title>
            <SubTitle>
              {localizedRefusalMessage}
              <ResourceLink refusalTag={refusalTag} />
            </SubTitle>
          </Column>
          <OpenHelpButton>{locales.help}</OpenHelpButton>
        </ModalContent>
      </Modal>
    );
  }
  return (
    <Page withNoBackButton>
      <OnboardingPageContent>
        <Column alignItems="center" textAlign="center">
          <ImageContainer withMarginTop>
            <DecorativeImage heightPx={80} src={invalidKycSrc} widthPx={80} />
          </ImageContainer>
          <Title>{locales.title}</Title>
          <SubTitle>{locales.subtitle}</SubTitle>
          <Button onClick={() => setIsModalOpen(true)} variant="secondary">
            {locales.cta}
          </Button>
        </Column>
      </OnboardingPageContent>
    </Page>
  );
};

export default CannotOpenAccount;
